/**
 * Schedule Calendar Page - DRR-233 Track D
 *
 * Operations view for scheduled tasks and workflows.
 * Shows active schedules, life-critical schedules, and due schedules.
 */

import { useState } from 'react';
import {
  Container,
  Title,
  Card,
  Text,
  Badge,
  Group,
  Stack,
  Button,
  Table,
  Alert,
  ActionIcon,
  Tooltip,
  Tabs,
  SimpleGrid,
  ThemeIcon,
  Loader,
  Modal,
  Textarea,
  Select,
} from '@mantine/core';
import {
  IconRefresh,
  IconAlertTriangle,
  IconClock,
  IconPlayerPlay,
  IconPlayerPause,
  IconPlayerStop,
  IconCalendar,
  IconArchive,
  IconHeartbeat,
  IconClockHour4,
} from '@tabler/icons-react';
import {
  useGetActiveSchedulesOpsQuery,
  useGetLifeCriticalSchedulesOpsQuery,
  useGetDueSchedulesOpsQuery,
  useListSchedulesQuery,
  usePauseScheduleOpsMutation,
  useResumeScheduleOpsMutation,
  useDisableScheduleOpsMutation,
  useTriggerScheduleNowOpsMutation,
  useArchiveScheduleMutation,
  GqlScheduleStatus,
  GqlSchedulePriority,
  GqlScheduleType,
} from '@/generated/graphql';
import { notifications } from '@mantine/notifications';

function getStatusBadge(status: GqlScheduleStatus | string) {
  switch (status) {
    case GqlScheduleStatus.ACTIVE:
    case 'ACTIVE':
      return <Badge color="green" leftSection={<IconPlayerPlay size={12} />}>Active</Badge>;
    case GqlScheduleStatus.PAUSED:
    case 'PAUSED':
      return <Badge color="yellow" leftSection={<IconPlayerPause size={12} />}>Paused</Badge>;
    case GqlScheduleStatus.DISABLED:
    case 'DISABLED':
      return <Badge color="gray" leftSection={<IconPlayerStop size={12} />}>Disabled</Badge>;
    case GqlScheduleStatus.EXPIRED:
    case 'EXPIRED':
      return <Badge color="red">Expired</Badge>;
    case GqlScheduleStatus.FAILED:
    case 'FAILED':
      return <Badge color="red">Failed</Badge>;
    default:
      return <Badge color="gray">{status}</Badge>;
  }
}

function getPriorityBadge(priority: GqlSchedulePriority | string) {
  switch (priority) {
    case GqlSchedulePriority.LIFE_CRITICAL:
    case 'LIFE_CRITICAL':
      return <Badge color="red" leftSection={<IconHeartbeat size={12} />}>Life Critical</Badge>;
    case GqlSchedulePriority.HIGH:
    case 'HIGH':
      return <Badge color="orange">High</Badge>;
    case GqlSchedulePriority.NORMAL:
    case 'NORMAL':
      return <Badge color="blue">Normal</Badge>;
    case GqlSchedulePriority.LOW:
    case 'LOW':
      return <Badge color="gray">Low</Badge>;
    default:
      return <Badge color="gray">{priority}</Badge>;
  }
}

function getTypeBadge(type: GqlScheduleType | string) {
  switch (type) {
    case GqlScheduleType.CRON:
    case 'CRON':
      return <Badge variant="outline">Cron</Badge>;
    case GqlScheduleType.INTERVAL:
    case 'INTERVAL':
      return <Badge variant="outline">Interval</Badge>;
    case GqlScheduleType.ONE_TIME:
    case 'ONE_TIME':
      return <Badge variant="outline">One-Time</Badge>;
    case GqlScheduleType.ADAPTIVE:
    case 'ADAPTIVE':
      return <Badge variant="outline" color="violet">Adaptive</Badge>;
    case GqlScheduleType.EVENT_DRIVEN:
    case 'EVENT_DRIVEN':
      return <Badge variant="outline" color="cyan">Event-Driven</Badge>;
    default:
      return <Badge variant="outline">{type}</Badge>;
  }
}

export function ScheduleCalendarPage() {
  const [activeTab, setActiveTab] = useState<string | null>('overview');
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [archivingScheduleId, setArchivingScheduleId] = useState<string | null>(null);
  const [archiveReason, setArchiveReason] = useState('');

  // Queries
  const { data: activeData, loading: activeLoading, refetch: refetchActive } = useGetActiveSchedulesOpsQuery();
  const { data: lifeCriticalData, loading: lifeCriticalLoading, refetch: refetchLifeCritical } = useGetLifeCriticalSchedulesOpsQuery();
  const { data: dueData, loading: dueLoading, refetch: refetchDue } = useGetDueSchedulesOpsQuery({
    variables: { withinMinutes: 60 },
  });
  const { data: allData, loading: allLoading, refetch: refetchAll } = useListSchedulesQuery({
    variables: { limit: 100 },
  });

  // Mutations
  const [pauseSchedule, { loading: pausing }] = usePauseScheduleOpsMutation();
  const [resumeSchedule, { loading: resuming }] = useResumeScheduleOpsMutation();
  const [disableSchedule, { loading: disabling }] = useDisableScheduleOpsMutation();
  const [triggerNow, { loading: triggering }] = useTriggerScheduleNowOpsMutation();
  const [archiveSchedule, { loading: archiving }] = useArchiveScheduleMutation();

  const handleRefreshAll = () => {
    refetchActive();
    refetchLifeCritical();
    refetchDue();
    refetchAll();
  };

  const handlePause = async (scheduleId: string) => {
    try {
      await pauseSchedule({ variables: { scheduleId } });
      notifications.show({
        title: 'Schedule Paused',
        message: 'Schedule has been paused',
        color: 'yellow',
      });
      handleRefreshAll();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to pause schedule',
        color: 'red',
      });
    }
  };

  const handleResume = async (scheduleId: string) => {
    try {
      await resumeSchedule({ variables: { scheduleId } });
      notifications.show({
        title: 'Schedule Resumed',
        message: 'Schedule has been resumed',
        color: 'green',
      });
      handleRefreshAll();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to resume schedule',
        color: 'red',
      });
    }
  };

  const handleDisable = async (scheduleId: string) => {
    try {
      await disableSchedule({ variables: { scheduleId } });
      notifications.show({
        title: 'Schedule Disabled',
        message: 'Schedule has been disabled',
        color: 'gray',
      });
      handleRefreshAll();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to disable schedule',
        color: 'red',
      });
    }
  };

  const handleTriggerNow = async (scheduleId: string) => {
    try {
      await triggerNow({ variables: { scheduleId } });
      notifications.show({
        title: 'Schedule Triggered',
        message: 'Schedule has been triggered for immediate execution',
        color: 'blue',
      });
      handleRefreshAll();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to trigger schedule',
        color: 'red',
      });
    }
  };

  const handleOpenArchive = (scheduleId: string) => {
    setArchivingScheduleId(scheduleId);
    setArchiveReason('');
    setArchiveModalOpen(true);
  };

  const handleArchive = async () => {
    if (!archivingScheduleId || !archiveReason.trim()) return;
    try {
      await archiveSchedule({
        variables: {
          scheduleId: archivingScheduleId,
          reason: archiveReason.trim(),
        },
      });
      notifications.show({
        title: 'Schedule Archived',
        message: 'Schedule has been archived',
        color: 'gray',
      });
      setArchiveModalOpen(false);
      setArchivingScheduleId(null);
      setArchiveReason('');
      handleRefreshAll();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to archive schedule',
        color: 'red',
      });
    }
  };

  const activeSchedules = activeData?.getActiveSchedules ?? [];
  const lifeCriticalSchedules = lifeCriticalData?.getLifeCriticalSchedules ?? [];
  const dueSchedules = dueData?.getDueSchedules ?? [];
  const allSchedules = allData?.listSchedules?.items ?? [];

  const isLoading = activeLoading || lifeCriticalLoading || dueLoading || allLoading;

  return (
    <Container size="xl" py="md">
      {/* Archive Modal */}
      <Modal
        opened={archiveModalOpen}
        onClose={() => setArchiveModalOpen(false)}
        title="Archive Schedule"
      >
        <Stack>
          <Alert color="orange" icon={<IconArchive />}>
            Archiving a schedule will permanently disable it. This action requires a reason for audit compliance.
          </Alert>
          <Textarea
            label="Archive Reason"
            placeholder="Describe why this schedule is being archived..."
            value={archiveReason}
            onChange={(e) => setArchiveReason(e.currentTarget.value)}
            minRows={3}
            required
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setArchiveModalOpen(false)}>
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleArchive}
              loading={archiving}
              disabled={!archiveReason.trim()}
            >
              Archive Schedule
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Group justify="space-between" mb="lg">
        <Title order={2}>Schedule Management</Title>
        <Button
          leftSection={<IconRefresh size={16} />}
          onClick={handleRefreshAll}
          loading={isLoading}
        >
          Refresh All
        </Button>
      </Group>

      {/* Alerts */}
      {lifeCriticalSchedules.length > 0 && (
        <Alert color="red" icon={<IconHeartbeat />} mb="md">
          {lifeCriticalSchedules.length} life-critical schedule(s) active
        </Alert>
      )}

      {dueSchedules.length > 0 && (
        <Alert color="blue" icon={<IconClockHour4 />} mb="md">
          {dueSchedules.length} schedule(s) due within the next hour
        </Alert>
      )}

      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="overview" leftSection={<IconCalendar size={16} />}>
            Overview
          </Tabs.Tab>
          <Tabs.Tab
            value="life-critical"
            leftSection={<IconHeartbeat size={16} />}
            rightSection={lifeCriticalSchedules.length > 0 ? <Badge color="red" size="sm">{lifeCriticalSchedules.length}</Badge> : null}
          >
            Life Critical
          </Tabs.Tab>
          <Tabs.Tab
            value="due-soon"
            leftSection={<IconClockHour4 size={16} />}
            rightSection={dueSchedules.length > 0 ? <Badge color="blue" size="sm">{dueSchedules.length}</Badge> : null}
          >
            Due Soon
          </Tabs.Tab>
          <Tabs.Tab value="active" leftSection={<IconPlayerPlay size={16} />}>
            Active ({activeSchedules.length})
          </Tabs.Tab>
          <Tabs.Tab value="all" leftSection={<IconClock size={16} />}>
            All Schedules
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="overview" pt="md">
          <OverviewTab
            activeCount={activeSchedules.length}
            lifeCriticalCount={lifeCriticalSchedules.length}
            dueCount={dueSchedules.length}
            totalCount={allSchedules.length}
            loading={isLoading}
          />
        </Tabs.Panel>

        <Tabs.Panel value="life-critical" pt="md">
          <ScheduleTable
            schedules={lifeCriticalSchedules}
            loading={lifeCriticalLoading}
            onPause={handlePause}
            onResume={handleResume}
            onDisable={handleDisable}
            onTriggerNow={handleTriggerNow}
            onArchive={handleOpenArchive}
            pausing={pausing}
            resuming={resuming}
            disabling={disabling}
            triggering={triggering}
            emptyMessage="No life-critical schedules configured"
          />
        </Tabs.Panel>

        <Tabs.Panel value="due-soon" pt="md">
          <ScheduleTable
            schedules={dueSchedules}
            loading={dueLoading}
            onPause={handlePause}
            onResume={handleResume}
            onDisable={handleDisable}
            onTriggerNow={handleTriggerNow}
            onArchive={handleOpenArchive}
            pausing={pausing}
            resuming={resuming}
            disabling={disabling}
            triggering={triggering}
            emptyMessage="No schedules due in the next hour"
          />
        </Tabs.Panel>

        <Tabs.Panel value="active" pt="md">
          <ScheduleTable
            schedules={activeSchedules}
            loading={activeLoading}
            onPause={handlePause}
            onResume={handleResume}
            onDisable={handleDisable}
            onTriggerNow={handleTriggerNow}
            onArchive={handleOpenArchive}
            pausing={pausing}
            resuming={resuming}
            disabling={disabling}
            triggering={triggering}
            emptyMessage="No active schedules"
          />
        </Tabs.Panel>

        <Tabs.Panel value="all" pt="md">
          <ScheduleTable
            schedules={allSchedules}
            loading={allLoading}
            onPause={handlePause}
            onResume={handleResume}
            onDisable={handleDisable}
            onTriggerNow={handleTriggerNow}
            onArchive={handleOpenArchive}
            pausing={pausing}
            resuming={resuming}
            disabling={disabling}
            triggering={triggering}
            emptyMessage="No schedules configured"
          />
        </Tabs.Panel>
      </Tabs>
    </Container>
  );
}

interface OverviewTabProps {
  activeCount: number;
  lifeCriticalCount: number;
  dueCount: number;
  totalCount: number;
  loading: boolean;
}

function OverviewTab({
  activeCount,
  lifeCriticalCount,
  dueCount,
  totalCount,
  loading,
}: OverviewTabProps) {
  if (loading) {
    return (
      <Stack align="center" py="xl">
        <Loader size="lg" />
        <Text c="dimmed">Loading schedule status...</Text>
      </Stack>
    );
  }

  return (
    <Stack>
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
        <Card withBorder>
          <Group>
            <ThemeIcon size={60} radius="md" color="blue" variant="light">
              <IconClock size={30} />
            </ThemeIcon>
            <div>
              <Text size="sm" c="dimmed">Total Schedules</Text>
              <Text size="xl" fw={700}>{totalCount}</Text>
              <Text size="xs" c="dimmed">configured</Text>
            </div>
          </Group>
        </Card>

        <Card withBorder>
          <Group>
            <ThemeIcon size={60} radius="md" color="green" variant="light">
              <IconPlayerPlay size={30} />
            </ThemeIcon>
            <div>
              <Text size="sm" c="dimmed">Active</Text>
              <Text size="xl" fw={700} c="green">{activeCount}</Text>
              <Text size="xs" c="dimmed">running</Text>
            </div>
          </Group>
        </Card>

        <Card withBorder>
          <Group>
            <ThemeIcon size={60} radius="md" color="red" variant="light">
              <IconHeartbeat size={30} />
            </ThemeIcon>
            <div>
              <Text size="sm" c="dimmed">Life Critical</Text>
              <Text size="xl" fw={700} c="red">{lifeCriticalCount}</Text>
              <Text size="xs" c="dimmed">high priority</Text>
            </div>
          </Group>
        </Card>

        <Card withBorder>
          <Group>
            <ThemeIcon size={60} radius="md" color="blue" variant="light">
              <IconClockHour4 size={30} />
            </ThemeIcon>
            <div>
              <Text size="sm" c="dimmed">Due Soon</Text>
              <Text size="xl" fw={700}>{dueCount}</Text>
              <Text size="xs" c="dimmed">within 1 hour</Text>
            </div>
          </Group>
        </Card>
      </SimpleGrid>
    </Stack>
  );
}

interface ScheduleTableProps {
  schedules: Array<{
    scheduleId: string;
    scheduleName: string;
    scheduleCode?: string | null;
    scheduleType?: GqlScheduleType | string;
    scheduleStatus?: GqlScheduleStatus | string;
    schedulePriority?: GqlSchedulePriority | string;
    cronExpression?: string | null;
    timezone?: string;
    createdAt?: string;
    updatedAt?: string;
  }>;
  loading: boolean;
  onPause: (scheduleId: string) => void;
  onResume: (scheduleId: string) => void;
  onDisable: (scheduleId: string) => void;
  onTriggerNow: (scheduleId: string) => void;
  onArchive: (scheduleId: string) => void;
  pausing: boolean;
  resuming: boolean;
  disabling: boolean;
  triggering: boolean;
  emptyMessage: string;
}

function ScheduleTable({
  schedules,
  loading,
  onPause,
  onResume,
  onDisable,
  onTriggerNow,
  onArchive,
  pausing,
  resuming,
  disabling,
  triggering,
  emptyMessage,
}: ScheduleTableProps) {
  if (loading) {
    return (
      <Stack align="center" py="xl">
        <Loader />
        <Text c="dimmed">Loading...</Text>
      </Stack>
    );
  }

  if (schedules.length === 0) {
    return (
      <Alert color="gray" icon={<IconClock />}>
        {emptyMessage}
      </Alert>
    );
  }

  return (
    <Table striped highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Name</Table.Th>
          <Table.Th>Type</Table.Th>
          <Table.Th>Status</Table.Th>
          <Table.Th>Priority</Table.Th>
          <Table.Th>Schedule</Table.Th>
          <Table.Th>Timezone</Table.Th>
          <Table.Th>Actions</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {schedules.map((schedule) => {
          const isActive = schedule.scheduleStatus === GqlScheduleStatus.ACTIVE || schedule.scheduleStatus === 'ACTIVE';
          const isPaused = schedule.scheduleStatus === GqlScheduleStatus.PAUSED || schedule.scheduleStatus === 'PAUSED';
          const isDisabled = schedule.scheduleStatus === GqlScheduleStatus.DISABLED || schedule.scheduleStatus === 'DISABLED';

          return (
            <Table.Tr key={schedule.scheduleId}>
              <Table.Td>
                <Text fw={500}>{schedule.scheduleName}</Text>
                {schedule.scheduleCode && (
                  <Text size="xs" c="dimmed">{schedule.scheduleCode}</Text>
                )}
              </Table.Td>
              <Table.Td>
                {getTypeBadge(schedule.scheduleType ?? 'UNKNOWN')}
              </Table.Td>
              <Table.Td>
                {getStatusBadge(schedule.scheduleStatus ?? 'UNKNOWN')}
              </Table.Td>
              <Table.Td>
                {getPriorityBadge(schedule.schedulePriority ?? 'NORMAL')}
              </Table.Td>
              <Table.Td>
                <Text size="sm" ff="monospace">{schedule.cronExpression ?? '-'}</Text>
              </Table.Td>
              <Table.Td>
                <Text size="sm">{schedule.timezone ?? 'UTC'}</Text>
              </Table.Td>
              <Table.Td>
                <Group gap="xs">
                  {isActive && (
                    <>
                      <Tooltip label="Pause Schedule">
                        <ActionIcon
                          variant="light"
                          color="yellow"
                          onClick={() => onPause(schedule.scheduleId)}
                          loading={pausing}
                        >
                          <IconPlayerPause size={16} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Trigger Now">
                        <ActionIcon
                          variant="light"
                          color="blue"
                          onClick={() => onTriggerNow(schedule.scheduleId)}
                          loading={triggering}
                        >
                          <IconPlayerPlay size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </>
                  )}
                  {isPaused && (
                    <Tooltip label="Resume Schedule">
                      <ActionIcon
                        variant="light"
                        color="green"
                        onClick={() => onResume(schedule.scheduleId)}
                        loading={resuming}
                      >
                        <IconPlayerPlay size={16} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                  {!isDisabled && (
                    <>
                      <Tooltip label="Disable Schedule">
                        <ActionIcon
                          variant="light"
                          color="gray"
                          onClick={() => onDisable(schedule.scheduleId)}
                          loading={disabling}
                        >
                          <IconPlayerStop size={16} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Archive Schedule">
                        <ActionIcon
                          variant="light"
                          color="red"
                          onClick={() => onArchive(schedule.scheduleId)}
                        >
                          <IconArchive size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </>
                  )}
                </Group>
              </Table.Td>
            </Table.Tr>
          );
        })}
      </Table.Tbody>
    </Table>
  );
}

export default ScheduleCalendarPage;
